import React, { useCallback, useEffect } from "react";
import {
  Review,
  ReviewOverviewSnapshot,
  ReviewReplyStatusCounts,
  ReviewServices,
  PlayersServices,
} from "@/services/bedrock";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { URLS, usePaginatedCombobox, useSegmentation } from "@/utils";
import { periodToDateRange } from "../periodToDateRange";
import { reputationTabSearchMemory } from "../reputationTabSearchMemory";
import {
  SNAPSHOT_QUERY_PARAM,
  buildVolumeSeries,
  findSnapshotByDate,
  formatSnapshotLabel,
  snapshotMetricsUnchanged,
  useOverviewUrlParams,
  type SnapshotOption,
  type SnapshotPeriod,
  type VolumeSeries,
} from "./snapshotHelpers";
import { OverviewFilters } from "./OverviewFilters";
import { PeriodHealth } from "./PeriodHealth";
import { PeriodInsights } from "./PeriodInsights";
import { PlayersTable, type PlayerSortKey } from "./PlayersTable";
import { EvidenceReviews } from "./EvidenceReviews";
import { formatOverviewPeriodFilter, formatOverviewScopeFilter } from "../reportExportHelpers";

type OverviewDisplay = {
  period: SnapshotPeriod;
  isAllTime: boolean;
  playerId: number | null;
  segmentationDetailId: number | null;
  showCoachShare: boolean;
  showPlayersTable: boolean;
  playerLabel?: string | null;
  snapshot?: ReviewOverviewSnapshot | null;
  previousSnapshot?: ReviewOverviewSnapshot | null;
  allTimeSnapshot?: ReviewOverviewSnapshot | null;
  coachPeriodSnapshot?: ReviewOverviewSnapshot | null;
  coachAllTimeSnapshot?: ReviewOverviewSnapshot | null;
  volumeSeries?: VolumeSeries;
  replyCounts?: ReviewReplyStatusCounts | null;
  previousReplyCounts?: ReviewReplyStatusCounts | null;
  allTimeReplyCounts?: ReviewReplyStatusCounts | null;
  coachPeriodReplyCounts?: ReviewReplyStatusCounts | null;
  coachAllTimeReplyCounts?: ReviewReplyStatusCounts | null;
  playersContent?: ReviewOverviewSnapshot[];
  playersTotalCount?: number;
  topReviews?: Review[];
  bottomReviews?: Review[];
  navItems: SnapshotOption[];
  navValue: SnapshotOption | null;
  periodDate: string | null;
  exportFilters: string[];
};

function querySettled(enabled: boolean, isPending: boolean, isPlaceholderData: boolean): boolean {
  return !enabled || (!isPending && !isPlaceholderData);
}

export function Overview() {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const urlParams = useOverviewUrlParams();

  const [period, setPeriod] = React.useState<SnapshotPeriod>(urlParams.period);
  const [playerId, setPlayerId] = React.useState<number | null>(urlParams.playerId);
  const [segmentationDetailId, setSegmentationDetailId] = React.useState<number | null>(urlParams.segmentationDetailId);
  const [playerSort, setPlayerSort] = React.useState<PlayerSortKey>("volume");
  const [playerSortOrder, setPlayerSortOrder] = React.useState<"asc" | "desc">("desc");

  const preferredDateRef = React.useRef<string | null>(urlParams.date);
  const scopeKey = `${playerId ?? "coach"}:${segmentationDetailId ?? "none"}`;
  const selectionForScopeRef = React.useRef<string | null>(null);
  const [loadedScopeKey, setLoadedScopeKey] = React.useState<string | null>(null);
  const [isRefreshingUi, setIsRefreshingUi] = React.useState(false);
  const [settledDisplay, setSettledDisplay] = React.useState<OverviewDisplay | null>(null);
  const blockEnsureUntilReloadRef = React.useRef(false);
  /** True after a scope change reset until the combobox fetch has actually started. */
  const awaitingScopeReloadStartRef = React.useRef(false);
  const showCoachShare = playerId != null || segmentationDetailId != null;

  const markScopeLoaded = useCallback((key: string) => {
    selectionForScopeRef.current = key;
    setLoadedScopeKey(key);
  }, []);

  const fetchSnapshots = useCallback(
    async (periodType: "monthly" | "weekly", _search: string | undefined, page: number) => {
      const result = await ReviewServices.getReviewSnapshots({
        playerId,
        segmentationDetailId,
        periodType,
        page: page + 1,
        perPage: 25,
      });

      return {
        ...result,
        content: result.content.map(snapshot => ({
          ...snapshot,
          label: formatSnapshotLabel(snapshot),
        })),
      };
    },
    [playerId, segmentationDetailId]
  );

  const fetchMonthlySnapshots = useCallback(
    (search: string | undefined, page: number) => fetchSnapshots("monthly", search, page),
    [fetchSnapshots]
  );

  const fetchWeeklySnapshots = useCallback(
    (search: string | undefined, page: number) => fetchSnapshots("weekly", search, page),
    [fetchSnapshots]
  );

  const monthlySnapshots = usePaginatedCombobox(fetchMonthlySnapshots, false);
  const weeklySnapshots = usePaginatedCombobox(fetchWeeklySnapshots, false);
  const isAllTime = period === "all_time";
  const activeSnapshots = period === "weekly" ? weeklySnapshots : monthlySnapshots;
  const lastScopeKey = React.useRef(scopeKey);
  useEffect(() => {
    if (lastScopeKey.current === scopeKey) return;
    lastScopeKey.current = scopeKey;
    selectionForScopeRef.current = null;
    // Keep the previously settled display visible (disabled) until the new scope is fully ready.

    if (!isAllTime && activeSnapshots.value?.periodDate) {
      preferredDateRef.current = activeSnapshots.value.periodDate.slice(0, 10);
    }

    blockEnsureUntilReloadRef.current = !isAllTime;
    awaitingScopeReloadStartRef.current = !isAllTime;
    monthlySnapshots.reset();
    weeklySnapshots.reset();
  }, [scopeKey, isAllTime, monthlySnapshots, weeklySnapshots, activeSnapshots.value?.periodDate]);

  const { data: selectedPlayer } = useQuery({
    queryKey: ["overview-player", playerId],
    queryFn: async () => {
      const player = await PlayersServices.getPlayer(playerId!);
      return {
        id: player.id,
        fullName: player.fullName || "No Name",
      };
    },
    enabled: playerId != null,
  });

  const selectPlayer = useCallback((nextPlayerId: number) => {
    setSegmentationDetailId(null);
    setPlayerId(nextPlayerId);
  }, []);

  const selectSegmentation = useCallback((nextId: number) => {
    setPlayerId(null);
    setSegmentationDetailId(nextId);
  }, []);

  const clearScope = useCallback(() => {
    setPlayerId(null);
    setSegmentationDetailId(null);
  }, []);

  const scopeKeyRef = React.useRef(scopeKey);
  scopeKeyRef.current = scopeKey;

  const ensurePeriodSelected = useCallback(
    async (targetPeriod: SnapshotPeriod) => {
      if (targetPeriod === "all_time") return;

      const requestedScopeKey = scopeKey;
      const snapshots = targetPeriod === "weekly" ? weeklySnapshots : monthlySnapshots;
      if (snapshots.isLoadingMore) return;
      if (snapshots.items.length === 0) {
        snapshots.onValueChange(null, undefined);
        markScopeLoaded(requestedScopeKey);
        return;
      }

      const preferredDate = preferredDateRef.current;
      if (preferredDate) {
        const fromList = findSnapshotByDate(snapshots.items, preferredDate);
        if (fromList) {
          preferredDateRef.current = null;
          snapshots.onValueChange(fromList, undefined);
          markScopeLoaded(requestedScopeKey);
          return;
        }

        try {
          const result = await ReviewServices.getReviewSnapshots({
            periodType: targetPeriod,
            date: preferredDate,
            playerId,
            segmentationDetailId,
            page: 1,
            perPage: 1,
          });
          if (scopeKeyRef.current !== requestedScopeKey) return;
          const matched = result.content[0];
          if (matched) {
            preferredDateRef.current = null;
            snapshots.onValueChange({ ...matched, label: formatSnapshotLabel(matched) }, undefined);
            markScopeLoaded(requestedScopeKey);
            return;
          }
        } catch {
          if (scopeKeyRef.current !== requestedScopeKey) return;
          // fall through to latest
        }
        preferredDateRef.current = null;
      }

      if (scopeKeyRef.current !== requestedScopeKey) return;

      const valueBelongsToList =
        snapshots.value != null && snapshots.items.some(item => item.id === snapshots.value?.id);
      if (!valueBelongsToList) {
        snapshots.onValueChange(snapshots.items[0], undefined);
      }
      markScopeLoaded(requestedScopeKey);
    },
    [weeklySnapshots, monthlySnapshots, scopeKey, playerId, segmentationDetailId, markScopeLoaded]
  );

  useEffect(() => {
    if (isAllTime) return;

    if (blockEnsureUntilReloadRef.current) {
      if (activeSnapshots.isLoadingMore) {
        awaitingScopeReloadStartRef.current = false;
        return;
      }
      // Reset was requested but the fetch hasn't started yet — wait.
      if (awaitingScopeReloadStartRef.current) return;
      // Finished reloading for the new scope (possibly with an empty list).
      blockEnsureUntilReloadRef.current = false;
    }

    if (selectionForScopeRef.current === scopeKey && activeSnapshots.value != null) return;
    void ensurePeriodSelected(period);
  }, [
    scopeKey,
    period,
    isAllTime,
    activeSnapshots.value,
    activeSnapshots.items,
    activeSnapshots.isLoadingMore,
    ensurePeriodSelected,
  ]);

  const handlePeriodChange = useCallback(
    (newPeriod: SnapshotPeriod) => {
      setPeriod(newPeriod);
      preferredDateRef.current = null;
      blockEnsureUntilReloadRef.current = false;
      if (newPeriod === "all_time") {
        // all_time readiness is marked when its query settles
        selectionForScopeRef.current = null;
        setLoadedScopeKey(null);
        return;
      }

      const snapshots = newPeriod === "weekly" ? weeklySnapshots : monthlySnapshots;
      if (snapshots.items.length > 0) {
        snapshots.onValueChange(snapshots.items[0], undefined);
        markScopeLoaded(scopeKey);
      } else {
        // Keep showing the previous settled view until this period's list loads.
        selectionForScopeRef.current = null;
        setLoadedScopeKey(null);
      }
    },
    [weeklySnapshots, monthlySnapshots, scopeKey, markScopeLoaded]
  );

  const {
    data: allTimeSnapshots,
    isPending: isAllTimePending,
    isPlaceholderData: isAllTimePlaceholder,
  } = useQuery({
    queryKey: ["review-snapshot", "all_time", scopeKey],
    queryFn: () =>
      ReviewServices.getReviewSnapshots({
        periodType: "all_time",
        playerId,
        segmentationDetailId,
      }),
    placeholderData: keepPreviousData,
  });
  const allTimeSnapshot = allTimeSnapshots?.content[0];
  const liveSnapshot = isAllTime ? allTimeSnapshot : activeSnapshots.value;

  useEffect(() => {
    if (!isAllTime || !allTimeSnapshot || isAllTimePlaceholder || isAllTimePending) return;
    markScopeLoaded(scopeKey);
  }, [isAllTime, allTimeSnapshot, isAllTimePlaceholder, isAllTimePending, scopeKey, markScopeLoaded]);

  // Sync filters to URL (all-time omits period params).
  // Only while Overview is the active route — otherwise this can clobber Reviews search params
  // when navigating away via history.push before the tab component unmounts.
  useEffect(() => {
    const tabSegment = location.pathname.replace(/^\/reputation\/?/, "").split("/")[0] || "overview";
    if (tabSegment !== "overview") return;
    if (selectionForScopeRef.current !== scopeKey) return;
    if (!liveSnapshot && !isAllTime) return;
    if (isAllTime && !allTimeSnapshot) return;

    const searchParams = new URLSearchParams();
    if (!isAllTime && liveSnapshot) {
      const periodDate = liveSnapshot.periodDate.slice(0, 10);
      if (period === "monthly" || period === "weekly") {
        searchParams.set(SNAPSHOT_QUERY_PARAM[period], periodDate);
      }
    }
    if (playerId != null) searchParams.set("playerId", String(playerId));
    if (segmentationDetailId != null) {
      searchParams.set("segmentationDetailId", String(segmentationDetailId));
    }

    const search = searchParams.toString();
    const nextSearch = search ? `?${search}` : "";
    if (location.search === nextSearch) return;

    history.replace({
      pathname: location.pathname.includes("/overview") ? location.pathname : URLS.coach.reputation.overview,
      search: nextSearch,
    });
  }, [
    liveSnapshot,
    allTimeSnapshot,
    isAllTime,
    period,
    playerId,
    segmentationDetailId,
    location.pathname,
    location.search,
    history,
    scopeKey,
  ]);

  const currentIndex =
    !isAllTime && liveSnapshot ? activeSnapshots.items.findIndex(item => item.id === liveSnapshot.id) : -1;
  const previousSnapshot = !isAllTime && currentIndex >= 0 ? activeSnapshots.items[currentIndex + 1] ?? null : null;
  const volumeSeries = React.useMemo(
    () =>
      buildVolumeSeries({
        period,
        selected: liveSnapshot,
        monthlySnapshots: monthlySnapshots.items,
        weeklySnapshots: weeklySnapshots.items,
      }),
    [period, liveSnapshot, monthlySnapshots.items, weeklySnapshots.items]
  );
  const periodDate = isAllTime ? null : liveSnapshot?.periodDate?.slice(0, 10) ?? null;
  const dateRange =
    !isAllTime && periodDate && (period === "monthly" || period === "weekly")
      ? periodToDateRange(period, periodDate)
      : null;

  const {
    data: coachPeriodPage,
    isPending: isCoachPeriodPending,
    isPlaceholderData: isCoachPeriodPlaceholder,
  } = useQuery({
    queryKey: ["review-snapshot", "coach-period", period, periodDate],
    queryFn: () =>
      ReviewServices.getReviewSnapshots({
        playerId: null,
        segmentationDetailId: null,
        periodType: period,
        date: periodDate ?? undefined,
        page: 1,
        perPage: 1,
      }),
    enabled: showCoachShare && !isAllTime && !!periodDate,
    placeholderData: keepPreviousData,
  });

  const {
    data: coachAllTimePage,
    isPending: isCoachAllTimePending,
    isPlaceholderData: isCoachAllTimePlaceholder,
  } = useQuery({
    queryKey: ["review-snapshot", "coach-all-time"],
    queryFn: () =>
      ReviewServices.getReviewSnapshots({
        playerId: null,
        segmentationDetailId: null,
        periodType: "all_time",
      }),
    enabled: showCoachShare,
    placeholderData: keepPreviousData,
  });
  const coachAllTimeSnapshot = coachAllTimePage?.content[0];
  const coachPeriodSnapshot = isAllTime ? coachAllTimeSnapshot : coachPeriodPage?.content[0];

  const previousPeriodDate = previousSnapshot?.periodDate?.slice(0, 10) ?? null;
  const previousDateRange =
    !isAllTime && previousPeriodDate && (period === "monthly" || period === "weekly")
      ? periodToDateRange(period, previousPeriodDate)
      : null;

  const replyScopeParams = {
    playerId: playerId ?? undefined,
    segmentationIds: segmentationDetailId != null ? [segmentationDetailId] : undefined,
  };

  const replyPeriodEnabled = isAllTime || !!dateRange;
  const {
    data: replyCounts,
    isPending: isReplyCountsPending,
    isPlaceholderData: isReplyCountsPlaceholder,
  } = useQuery({
    queryKey: [
      "reviews",
      "reply-status-counts",
      scopeKey,
      isAllTime ? "all_time" : `${dateRange?.startDate}:${dateRange?.endDate}`,
    ],
    queryFn: () =>
      ReviewServices.getReplyStatusCounts({
        ...replyScopeParams,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      }),
    enabled: replyPeriodEnabled,
    placeholderData: keepPreviousData,
  });

  const previousReplyEnabled = !!previousDateRange;
  const {
    data: previousReplyCounts,
    isPending: isPreviousReplyPending,
    isPlaceholderData: isPreviousReplyPlaceholder,
  } = useQuery({
    queryKey: [
      "reviews",
      "reply-status-counts",
      "previous",
      scopeKey,
      previousDateRange?.startDate,
      previousDateRange?.endDate,
    ],
    queryFn: () =>
      ReviewServices.getReplyStatusCounts({
        ...replyScopeParams,
        startDate: previousDateRange!.startDate,
        endDate: previousDateRange!.endDate,
      }),
    enabled: previousReplyEnabled,
    placeholderData: keepPreviousData,
  });

  const allTimeReplyEnabled = !isAllTime;
  const {
    data: allTimeReplyCounts,
    isPending: isAllTimeReplyPending,
    isPlaceholderData: isAllTimeReplyPlaceholder,
  } = useQuery({
    queryKey: ["reviews", "reply-status-counts", "all_time", scopeKey],
    queryFn: () => ReviewServices.getReplyStatusCounts(replyScopeParams),
    enabled: allTimeReplyEnabled,
    placeholderData: keepPreviousData,
  });

  const coachPeriodReplyEnabled = showCoachShare && (isAllTime || !!dateRange);
  const {
    data: coachPeriodReplyCounts,
    isPending: isCoachPeriodReplyPending,
    isPlaceholderData: isCoachPeriodReplyPlaceholder,
  } = useQuery({
    queryKey: [
      "reviews",
      "reply-status-counts",
      "coach",
      isAllTime ? "all_time" : `${dateRange?.startDate}:${dateRange?.endDate}`,
    ],
    queryFn: () =>
      ReviewServices.getReplyStatusCounts({
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      }),
    enabled: coachPeriodReplyEnabled,
    placeholderData: keepPreviousData,
  });

  const coachAllTimeReplyEnabled = showCoachShare && !isAllTime;
  const {
    data: coachAllTimeReplyCounts,
    isPending: isCoachAllTimeReplyPending,
    isPlaceholderData: isCoachAllTimeReplyPlaceholder,
  } = useQuery({
    queryKey: ["reviews", "reply-status-counts", "coach", "all_time"],
    queryFn: () => ReviewServices.getReplyStatusCounts(),
    enabled: coachAllTimeReplyEnabled,
    placeholderData: keepPreviousData,
  });

  const showPlayersTable = playerId == null;
  const playersQueryEnabled = showPlayersTable && (isAllTime || !!periodDate);
  const {
    data: playersPage,
    isPending: isPlayersPending,
    isPlaceholderData: isPlayersPlaceholder,
  } = useQuery({
    queryKey: [
      "review-snapshot",
      "players-table",
      period,
      periodDate,
      segmentationDetailId,
      playerSort,
      playerSortOrder,
    ],
    queryFn: () =>
      ReviewServices.getReviewSnapshots({
        entityScope: "players",
        periodType: period,
        date: periodDate ?? undefined,
        withinSegmentationDetailId: segmentationDetailId ?? undefined,
        sort: playerSort,
        order: playerSortOrder,
        page: 1,
        perPage: 5,
      }),
    enabled: playersQueryEnabled,
    placeholderData: keepPreviousData,
  });

  const reviewListBaseParams = {
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate,
    playerId: playerId ?? undefined,
    segmentationIds: segmentationDetailId != null ? [segmentationDetailId] : undefined,
    sort: "sentiment_shareability" as const,
    page: 1,
    perPage: 5,
  };

  const reviewsQueryEnabled = isAllTime || !!dateRange;
  const {
    data: topReviews,
    isPending: isTopReviewsPending,
    isPlaceholderData: isTopReviewsPlaceholder,
  } = useQuery({
    queryKey: ["reviews", "overview-top", period, periodDate, scopeKey],
    queryFn: () =>
      ReviewServices.getReviews({
        ...reviewListBaseParams,
        sentiment: "positive",
        order: "desc",
      }),
    enabled: reviewsQueryEnabled,
    placeholderData: keepPreviousData,
  });

  const {
    data: bottomReviews,
    isPending: isBottomReviewsPending,
    isPlaceholderData: isBottomReviewsPlaceholder,
  } = useQuery({
    queryKey: ["reviews", "overview-bottom", period, periodDate, scopeKey],
    queryFn: () =>
      ReviewServices.getReviews({
        ...reviewListBaseParams,
        sentiment: ["negative", "neutral"],
        order: "asc",
      }),
    enabled: reviewsQueryEnabled,
    placeholderData: keepPreviousData,
  });

  const { segmentationOptions } = useSegmentation(["BRANCH", "REGION"]);
  const segmentationLabel =
    segmentationOptions.find(option => Number(option.value) === segmentationDetailId)?.text ?? null;
  const playerLabel = selectedPlayer?.fullName;
  const overviewExportFilters = React.useMemo(
    () => [
      formatOverviewPeriodFilter(period, periodDate),
      formatOverviewScopeFilter({
        playerLabel,
        segmentationLabel,
      }),
    ],
    [period, periodDate, playerLabel, segmentationLabel]
  );

  const snapshotSelectionReady = loadedScopeKey === scopeKey && !isRefreshingUi;
  const secondaryReady =
    querySettled(true, isAllTimePending, isAllTimePlaceholder) &&
    querySettled(showCoachShare && !isAllTime && !!periodDate, isCoachPeriodPending, isCoachPeriodPlaceholder) &&
    querySettled(showCoachShare, isCoachAllTimePending, isCoachAllTimePlaceholder) &&
    querySettled(replyPeriodEnabled, isReplyCountsPending, isReplyCountsPlaceholder) &&
    querySettled(previousReplyEnabled, isPreviousReplyPending, isPreviousReplyPlaceholder) &&
    querySettled(allTimeReplyEnabled, isAllTimeReplyPending, isAllTimeReplyPlaceholder) &&
    querySettled(coachPeriodReplyEnabled, isCoachPeriodReplyPending, isCoachPeriodReplyPlaceholder) &&
    querySettled(coachAllTimeReplyEnabled, isCoachAllTimeReplyPending, isCoachAllTimeReplyPlaceholder) &&
    querySettled(playersQueryEnabled, isPlayersPending, isPlayersPlaceholder) &&
    querySettled(reviewsQueryEnabled, isTopReviewsPending, isTopReviewsPlaceholder) &&
    querySettled(reviewsQueryEnabled, isBottomReviewsPending, isBottomReviewsPlaceholder);

  // Monthly/weekly empty scopes are ready once selection is marked (snapshot may be null).
  const liveViewReady =
    snapshotSelectionReady &&
    secondaryReady &&
    (isAllTime || !activeSnapshots.isLoadingMore || activeSnapshots.items.length > 0 || liveSnapshot == null);

  const liveDisplay = React.useMemo<OverviewDisplay>(
    () => ({
      period,
      isAllTime,
      playerId,
      segmentationDetailId,
      showCoachShare,
      showPlayersTable,
      playerLabel,
      snapshot: liveSnapshot,
      previousSnapshot,
      allTimeSnapshot: isAllTime ? null : allTimeSnapshot,
      coachPeriodSnapshot,
      coachAllTimeSnapshot: isAllTime ? null : coachAllTimeSnapshot,
      volumeSeries,
      replyCounts,
      previousReplyCounts,
      allTimeReplyCounts,
      coachPeriodReplyCounts,
      coachAllTimeReplyCounts,
      playersContent: playersPage?.content,
      playersTotalCount: playersPage?.pagination?.totalCount,
      topReviews: topReviews?.content,
      bottomReviews: bottomReviews?.content,
      navItems: activeSnapshots.items,
      navValue: activeSnapshots.value ?? null,
      periodDate,
      exportFilters: overviewExportFilters,
    }),
    [
      period,
      isAllTime,
      playerId,
      segmentationDetailId,
      showCoachShare,
      showPlayersTable,
      playerLabel,
      liveSnapshot,
      previousSnapshot,
      allTimeSnapshot,
      coachPeriodSnapshot,
      coachAllTimeSnapshot,
      volumeSeries,
      replyCounts,
      previousReplyCounts,
      allTimeReplyCounts,
      coachPeriodReplyCounts,
      coachAllTimeReplyCounts,
      playersPage?.content,
      playersPage?.pagination?.totalCount,
      topReviews?.content,
      bottomReviews?.content,
      activeSnapshots.items,
      activeSnapshots.value,
      periodDate,
      overviewExportFilters,
    ]
  );

  useEffect(() => {
    if (!liveViewReady) return;
    setSettledDisplay(liveDisplay);
  }, [liveViewReady, liveDisplay]);

  const isHoldingPrevious = !liveViewReady && settledDisplay != null;
  const display = isHoldingPrevious ? settledDisplay! : liveDisplay;
  const isContentDisabled = isHoldingPrevious;

  const displaySnapshots = React.useMemo(
    () => ({
      ...activeSnapshots,
      items: display.navItems,
      value: display.navValue,
    }),
    [activeSnapshots, display.navItems, display.navValue]
  );

  const buildReviewsSearch = (extraParams?: Record<string, string>, options?: { allTime?: boolean }) => {
    const searchParams = new URLSearchParams();
    const useAllTimeDates = options?.allTime || display.isAllTime;
    const displayDateRange =
      !display.isAllTime && display.periodDate && (display.period === "monthly" || display.period === "weekly")
        ? periodToDateRange(display.period, display.periodDate)
        : null;
    if (!useAllTimeDates && displayDateRange) {
      searchParams.set("datePreset", "custom");
      searchParams.set("startDate", displayDateRange.startDate);
      searchParams.set("endDate", displayDateRange.endDate);
    }
    if (display.playerId != null) searchParams.set("playerId", String(display.playerId));
    if (display.segmentationDetailId != null) {
      searchParams.set("segmentationIds", String(display.segmentationDetailId));
    }
    Object.entries(extraParams ?? {}).forEach(([key, value]) => searchParams.set(key, value));
    return searchParams.toString();
  };

  const navigateToReviews = (extraParams?: Record<string, string>, options?: { allTime?: boolean }) => {
    reputationTabSearchMemory.overview = history.location.search;
    const search = buildReviewsSearch(extraParams, options);
    const searchWithPrefix = search ? `?${search}` : "";
    reputationTabSearchMemory.reviews = searchWithPrefix;
    history.push({
      pathname: URLS.coach.reputation.reviews,
      search: searchWithPrefix,
    });
  };

  const handlePlayerSortChange = (sort: PlayerSortKey, order: "asc" | "desc") => {
    setPlayerSort(sort);
    setPlayerSortOrder(order);
  };

  const applySyncedSnapshot = useCallback(
    async (synced: ReviewOverviewSnapshot) => {
      const labeled = { ...synced, label: formatSnapshotLabel(synced) };
      if (period === "weekly") {
        weeklySnapshots.onValueChange(labeled, undefined);
      } else if (period === "monthly") {
        monthlySnapshots.onValueChange(labeled, undefined);
      }
      // all_time snapshot comes from the query cache; invalidation below refreshes it
      markScopeLoaded(scopeKey);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["review-snapshot"] }),
        queryClient.invalidateQueries({ queryKey: ["reviews", "reply-status-counts"] }),
        queryClient.invalidateQueries({ queryKey: ["reviews", "overview-top"] }),
        queryClient.invalidateQueries({ queryKey: ["reviews", "overview-bottom"] }),
      ]);
      // Soft-reload period lists without clearing selection/items (avoids navigator flicker).
      monthlySnapshots.reload();
      weeklySnapshots.reload();
    },
    [period, weeklySnapshots, monthlySnapshots, markScopeLoaded, scopeKey, queryClient]
  );

  const refreshSnapshotMutation = useMutation({
    mutationFn: async () => {
      const snapshot = liveSnapshot;
      if (snapshot?.id == null || snapshot.id < 0) {
        throw "Overview is not ready to update yet. Please try again shortly.";
      }
      const before = snapshot;
      const refreshed = await ReviewServices.refreshReviewSnapshot(snapshot.id);
      return { before, refreshed };
    },
    onMutate: () => {
      setIsRefreshingUi(true);
    },
    onSuccess: async ({ before, refreshed }) => {
      try {
        if (snapshotMetricsUnchanged(before, refreshed)) {
          toast.info("No changes — overview was already up to date");
        } else {
          toast.success("Overview updated");
        }
        await applySyncedSnapshot(refreshed);
      } finally {
        setIsRefreshingUi(false);
      }
    },
    onError: (error: unknown) => {
      setIsRefreshingUi(false);
      toast.error(typeof error === "string" ? error : "Unable to update overview. Please try again.");
    },
  });

  return (
    <div className="flex w-full max-w-[1280px] flex-col gap-8 font-sans">
      <OverviewFilters
        period={period}
        onPeriodChange={handlePeriodChange}
        snapshots={displaySnapshots}
        playerId={playerId}
        playerLabel={selectedPlayer?.fullName}
        segmentationDetailId={segmentationDetailId}
        onSelectPlayer={selectPlayer}
        onSelectSegmentation={selectSegmentation}
        onClearScope={clearScope}
        disabled={isContentDisabled}
      />

      <PeriodHealth
        snapshot={display.snapshot}
        previousSnapshot={display.previousSnapshot}
        allTimeSnapshot={display.allTimeSnapshot}
        coachPeriodSnapshot={display.coachPeriodSnapshot}
        coachAllTimeSnapshot={display.coachAllTimeSnapshot}
        showCoachShare={display.showCoachShare}
        isAllTimePeriod={display.isAllTime}
        volumeSeries={display.volumeSeries}
        replyStatusCounts={display.replyCounts}
        previousReplyStatusCounts={display.previousReplyCounts}
        allTimeReplyStatusCounts={display.isAllTime ? display.replyCounts : display.allTimeReplyCounts}
        coachPeriodReplyStatusCounts={display.coachPeriodReplyCounts}
        coachAllTimeReplyStatusCounts={
          display.isAllTime ? display.coachPeriodReplyCounts : display.coachAllTimeReplyCounts
        }
        insights={
          <PeriodInsights
            summary={display.snapshot?.aiSummary}
            highlights={display.snapshot?.aiHighlights}
            generatedAt={display.snapshot?.aiGeneratedAt}
            showTeamHighlights={display.playerId == null}
          />
        }
        aiInsights={{
          summary: display.snapshot?.aiSummary,
          highlights: display.snapshot?.aiHighlights,
          generatedAt: display.snapshot?.aiGeneratedAt,
          showTeamHighlights: display.playerId == null,
        }}
        exportFilters={display.exportFilters}
        disabled={isContentDisabled}
        isRefreshingSnapshot={isRefreshingUi}
        onRefreshSnapshot={() => refreshSnapshotMutation.mutate()}
        onSnapshotSynced={applySyncedSnapshot}
        onNavigateToReviews={navigateToReviews}
      />

      {display.showPlayersTable && (
        <PlayersTable
          snapshots={display.playersContent}
          sort={playerSort}
          order={playerSortOrder}
          loading={isContentDisabled}
          totalCount={display.playersTotalCount}
          filterLabels={display.exportFilters}
          toExportParams={() => ({
            entityScope: "players",
            periodType: display.period,
            date: display.periodDate ?? undefined,
            withinSegmentationDetailId: display.segmentationDetailId ?? undefined,
            sort: playerSort,
            order: playerSortOrder,
          })}
          onSortChange={handlePlayerSortChange}
          onPlayerClick={selectPlayer}
        />
      )}

      <EvidenceReviews
        topReviews={display.topReviews}
        bottomReviews={display.bottomReviews}
        disabled={isContentDisabled}
        onViewAllTop={() => navigateToReviews({ sentiment: "positive", sort: "sentiment_shareability", order: "desc" })}
        onViewAllBottom={() =>
          navigateToReviews({ sentiment: "negative", sort: "sentiment_shareability", order: "asc" })
        }
        onReviewClick={(review: Review) => navigateToReviews({ reviewId: String(review.id) })}
      />
    </div>
  );
}
